import { useMemo } from 'react'
import { usePermissions } from '../context/PermissionContext'
import { useEntitlements } from '../context/EntitlementContext'
import {
  useIntegrationSummaryRegistered as useIntegrationSummary,
  isIntegrationConfigured,
} from '../featureHooks'
import { METRICS_SOURCE_KEYS } from '../../../../shared/features/rightsizing'

const item = (id, label, path, icon, options = {}) => ({ id, label, path, icon, ...options })

export function isNavigationItemActive(navigationItem, pathname) {
  if (navigationItem.activePrefixes) {
    return navigationItem.activePrefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
  }
  return pathname === navigationItem.path
}

/**
 * Single permission and entitlement aware navigation model shared by both
 * shells. Presentation may change, but route visibility never forks by skin.
 */
export default function useAppNavigation() {
  const { canRead, isAdmin, isGlobalAdmin, loading: permissionLoading } = usePermissions()
  const { isFeatureEnabled, showLicensePage, loading: entitlementLoading } = useEntitlements()
  const {
    summary: integrationSummary,
    loading: integrationSummaryLoading,
    error: integrationSummaryError,
  } = useIntegrationSummary()

  return useMemo(() => {
    const canReadLicensed = (section, feature) => canRead(section) && isFeatureEnabled(feature)
    // One shared signal for "the customer has this set up", keyed by entitlement
    // key, replacing four per-integration count reads. Permission and
    // configuration are now independent gates rather than one standing in for
    // the other: a configured integration the caller cannot read is hidden by
    // canRead, not by its count coming back zero.
    const configured = (entitlementKey) => isIntegrationConfigured(integrationSummary, entitlementKey)
    const integrationCount = (key) => (
      key === 'git'
        ? integrationSummary.git?.active || 0
        : integrationSummary[key]?.count || 0
    )

    const canAlerts = canReadLicensed('Alerts', 'alerts')
    const canInvestigate = canReadLicensed('CheckMK', 'investigate')
    const baseCanKubernetes = canReadLicensed('Kubernetes', 'kubernetes')
    const baseCanGitRepos = isGlobalAdmin() && isFeatureEnabled('git_repositories')
    const canCheckmk = canInvestigate && configured('checkmk')
    const canKubernetes = baseCanKubernetes && configured('kubernetes')
    const canGitRepos = baseCanGitRepos && configured('git_repositories')
    const canWazuh = canRead('Wazuh') && configured('wazuh_integration')
    const canVelociraptor = canReadLicensed('ApplianceConfig', 'appliance_app_velociraptor') && configured('appliance_app_velociraptor')
    const canRoutines = canRead('Alerts') && isFeatureEnabled('routines')
    const canVulnerabilityManagement = canRead('SecurityAppliances')
      && (isFeatureEnabled('vulnerability_scanning') || isFeatureEnabled('vulnerability_reporting'))
    const canHardening = canReadLicensed('SecurityAppliances', 'hardening')
    const canZeroTrust = canRead('VPCs') && isFeatureEnabled('cloudflare') && configured('cloudflare')
    // Rightsizing has nothing to analyse until a metrics source is set up, so
    // it follows the same licensed-AND-configured rule as every other
    // integration-backed entry. Asked of the shared source list rather than of
    // 'checkmk' by name: adding Zabbix or Datadog later must not require
    // editing the navigation.
    const canRightsizing = canReadLicensed('Rightsizing', 'rightsizing')
      && METRICS_SOURCE_KEYS.some((key) => configured(key))
    const hasExecutionDashboardAccess =
      canReadLicensed('Agents', 'agents') ||
      canReadLicensed('Playbooks', 'playbooks') ||
      canReadLicensed('Schedules', 'schedules') ||
      canReadLicensed('Queue', 'queue')
    const hasAppliancesAccess = canReadLicensed('Agents', 'agents')
    const hasLicensedIntegrationsAccess = canInvestigate || baseCanKubernetes || baseCanGitRepos || canRead('Wazuh')

    const sections = [
      {
        id: 'operations',
        label: 'Operations',
        homePath: canAlerts
          ? '/dashboard/alerts'
          : canInvestigate
            ? '/investigate'
            : canRoutines
              ? '/routines'
              : '/ai-governor',
        items: [
          canAlerts && item('alerts', 'Alerts', '/alerts', 'bell'),
          canInvestigate && item('investigate', 'Investigate', '/investigate', 'search', {
            activePrefixes: ['/investigate', '/cockpit'],
            children: [
              item('investigate-workspace', 'Workspace', '/investigate'),
              item('investigate-history', 'History', '/investigate/history'),
            ],
          }),
          canRoutines && item('routines', 'Routines', '/routines', 'clock', { activePrefixes: ['/routines'] }),
          canRead('Settings') && item('ai-governor', 'AI Governor', '/ai-governor', 'shield'),
        ].filter(Boolean),
      },
      {
        id: 'security',
        label: 'Security',
        homePath: (canVulnerabilityManagement || canHardening) ? '/dashboard/security' : '/security/zero-trust',
        items: [
          canVulnerabilityManagement && item(
            'vulnerability-management',
            'Vulnerability Management',
            '/security/vulnerability-management',
            'shield',
            { activePrefixes: ['/security/vulnerability-management'] },
          ),
          canHardening && item('hardening', 'Hardening', '/security/hardening', 'lock', { activePrefixes: ['/security/hardening'] }),
          canZeroTrust && item('zero-trust', 'Zero Trust', '/security/zero-trust', 'shield'),
        ].filter(Boolean),
      },
      {
        id: 'compliance',
        label: 'Compliance',
        items: [
          canReadLicensed('NIS2', 'nis2') && item('nis2', 'NIS2', '/compliance/nis2', 'notes'),
          canReadLicensed('ISO27001', 'iso27001') && item('iso27001', 'ISO 27001', '/compliance/iso27001', 'lock'),
          canReadLicensed('ComplianceCalendar', 'compliance_calendar') && item('compliance-calendar', 'Calendar', '/compliance/calendar', 'calendar'),
          canReadLicensed('Docs', 'docs') && item('docs', 'Documentation', '/docs', 'book'),
        ].filter(Boolean),
      },
      {
        id: 'execution',
        label: 'Execution',
        homePath: hasExecutionDashboardAccess ? '/dashboard/execution' : null,
        items: [
          (canReadLicensed('Agents', 'agents') || canReadLicensed('Queue', 'queue')) && item('agents-group', 'Agents', '/agents', 'terminal', {
            activePrefixes: ['/agents', '/queue'],
            children: [
              canReadLicensed('Agents', 'agents') && item('agents', 'Agents', '/agents'),
              canReadLicensed('Queue', 'queue') && item('queue', 'Queue', '/queue'),
            ].filter(Boolean),
          }),
          canReadLicensed('Playbooks', 'playbooks') && item('playbooks', 'Playbooks', '/playbooks', 'code'),
          item('jit', 'JIT', '/jit-access', 'lock', {
            activePrefixes: ['/jit-access', '/jit-admin'],
            children: [
              item('jit-access', 'JIT Access', '/jit-access'),
              (isGlobalAdmin() || isAdmin('JIT')) && item('jit-admin', 'JIT Admin', '/jit-admin'),
            ].filter(Boolean),
          }),
          canReadLicensed('Schedules', 'schedules') && item('schedules', 'Schedules', '/schedules', 'calendar'),
          canReadLicensed('Calendar', 'calendar') && item('calendar', 'Calendar', '/calendar', 'clock'),
          canReadLicensed('Calendar', 'calendar') && item('calendar-v2', 'Calendar V2', '/calendar/v2', 'calendar'),
        ].filter(Boolean),
      },
      {
        id: 'inventory',
        label: 'Inventory',
        items: [
          hasAppliancesAccess && item('appliances', 'Appliances', '/appliances', 'monitor', { activePrefixes: ['/appliances'] }),
          canReadLicensed('AssetOwnership', 'asset_ownership') && item('asset-ownership', 'Asset Ownership', '/asset-ownership', 'task'),
          canRightsizing && item('rightsizing', 'Rightsizing', '/rightsizing', 'chart'),
        ].filter(Boolean),
      },
      {
        id: 'integrations',
        label: 'Integrations',
        items: [
          integrationSummaryError && hasLicensedIntegrationsAccess && item('integrations-unavailable', 'Status unavailable', null, 'warning', { unavailable: true }),
          canCheckmk && item('checkmk', 'CheckMK', '/checkmk', 'monitor', { count: integrationCount('checkmk') }),
          canKubernetes && item('kubernetes', 'Kubernetes', '/kubernetes-clusters', 'globe', { count: integrationCount('kubernetes') }),
          canGitRepos && item('git-repositories', 'Git Repositories', '/git-repositories', 'code', { count: integrationCount('git') }),
          canWazuh && item('wazuh', 'Wazuh', '/wazuh', 'shield', { count: integrationCount('wazuh') }),
          canVelociraptor && item('velociraptor', 'Velociraptor', '/velociraptor', 'shield', { count: integrationCount('velociraptor') }),
        ].filter(Boolean),
      },
      {
        id: 'system',
        label: 'System',
        items: [
          canAlerts && item('usage', 'AI Value & Usage', '/usage', 'chart'),
          isAdmin('Users') && isFeatureEnabled('users') && item('users', 'Users', '/users', 'people'),
          isGlobalAdmin() && isFeatureEnabled('settings') && item('settings', 'Settings', '/settings', 'settings'),
          isGlobalAdmin() && showLicensePage && item('license', 'License', '/license', 'description'),
          isGlobalAdmin() && item('onboarding', 'Onboarding', '/onboarding', 'dashboard'),
        ].filter(Boolean),
      },
    ].filter((section) => section.items.length > 0)

    return {
      loading: permissionLoading || entitlementLoading || integrationSummaryLoading,
      sections,
    }
  }, [
    canRead,
    entitlementLoading,
    integrationSummary,
    integrationSummaryError,
    integrationSummaryLoading,
    isAdmin,
    isFeatureEnabled,
    isGlobalAdmin,
    permissionLoading,
    showLicensePage,
  ])
}
